import { isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import difference from 'lodash/difference';
import { JSM_PREMIUM_OFFERING_ID } from '@css-xp/ppc-utils';
import {
  CartProductSchema,
  LinkedEntitlementRelationShipType,
  ProductStatus,
  CartLinkedEntitlementsSchema,
} from '../../types';
import { State } from '../types';
import { isNewEntitlement } from './is-new-entitlement';

// This function is used to get the eligible add-ons for cancel when the product is a Statuspage product and has linked entitlements.
const getStatuspageEligibleAddonsForCancel = ({
  product,
  eligibleStatuspageAddons,
  linkedEntitlementsOfContainer,
}: {
  product: CartProductSchema;
  eligibleStatuspageAddons: any;
  linkedEntitlementsOfContainer: CartLinkedEntitlementsSchema[];
}) => {
  if (isStatuspage(product?.productId)) {
    // getting all offering ids in array which are eligible for updated offering id of parent statuspage
    const validStatuspageAddonsIds =
      eligibleStatuspageAddons?.map((addon: any) => {
        return addon?.offeringId;
      }) ?? [];

    /**
     * getting all linked entitlements product ids in array
     * we are converting productId = offeringId for statuspage addons and saving in store.
     * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
     */
    const linkedEntitlementsProductIds = linkedEntitlementsOfContainer.map(
      (linkedEntitlement) => linkedEntitlement?.productId // for statuspage
    );

    /**
     * we are checking if the linked entitlement product id is not in the valid statuspage addons ids
     * and we are going to make those linked entitlements to cancel
     */
    return difference(linkedEntitlementsProductIds, validStatuspageAddonsIds);
  }
  return [];
};

export const getUpdatedAddons = ({
  product,
  linkedEntitlements,
  selectedLinkedEntitlements = {},
  updatedOfferingId,
  updatedOfferingName = '',
  eligibleStatuspageAddons,
}: {
  product: CartProductSchema;
  linkedEntitlements: State['linkedEntitlements'];
  selectedLinkedEntitlements?: State['selectedLinkedEntitlements'];
  updatedOfferingId: string;
  updatedOfferingName?: string | null;
  eligibleStatuspageAddons?: any;
}) => {
  const linkedEntitlementsObj: State['linkedEntitlements'] = JSON.parse(JSON.stringify(linkedEntitlements));
  const selectedLinkedEntitlementsObj = JSON.parse(JSON.stringify(selectedLinkedEntitlements));

  /**
   * checking only for the JSM (Jira Service Management) product and Statuspage product
   * if the product is not JSM product and statuspage, then we return the same linkedEntitlements and selectedLinkedEntitlements
   */
  if (isJSMProduct(product?.productId) || isStatuspage(product?.productId)) {
    const productContainerId = product?.containerId ?? product.id;
    const linkedEntitlementsOfContainer = linkedEntitlementsObj[productContainerId] || [];

    const statuspageEligibleAddonsForCancel = getStatuspageEligibleAddonsForCancel({
      product,
      eligibleStatuspageAddons,
      linkedEntitlementsOfContainer,
    });

    /**
     *  if the updated offering is not JSM Premium, then check for the add-ons entitlements
     *  if
     */
    if (
      (isJSMProduct(product?.productId) && updatedOfferingId !== JSM_PREMIUM_OFFERING_ID) ||
      (isStatuspage(product?.productId) && statuspageEligibleAddonsForCancel?.length > 0)
    ) {
      linkedEntitlementsOfContainer.forEach((linkedEntitlement) => {
        // checking for the add-on linked entitlements
        if (linkedEntitlement?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON) {
          /**
           * if the linked add-on is a new product, then remove the linked entitlement from the cart when the JSM offering is not Premium
           * else update the product status to CANCEL with the reason that add-ons are not available on the selected JSM offering
           */

          if (isNewEntitlement(linkedEntitlement)) {
            // filtering and removing linked add-ons from the store when add-on is new product
            const filteredLinkedEntitlements = linkedEntitlementsObj[productContainerId].filter(
              (containerLinkedEntitlement) => {
                if (containerLinkedEntitlement?.id === linkedEntitlement?.id) {
                  // also removing from the selected linked entitlements
                  delete selectedLinkedEntitlementsObj?.[containerLinkedEntitlement?.id];
                  return false;
                }
                return true;
              }
            );
            linkedEntitlementsObj[productContainerId] = filteredLinkedEntitlements;
          } else {
            // here we are checking only existing linked add-ons to update the product status to CANCEL and adding the cancel reason
            const productStatus = selectedLinkedEntitlementsObj[linkedEntitlement.id]?.productStatuses ?? [];
            selectedLinkedEntitlementsObj[linkedEntitlement.id] = {
              ...selectedLinkedEntitlementsObj[linkedEntitlement.id],
              productStatuses: [...productStatus, ProductStatus.CANCEL], // keeping the existing product status and adding CANCEL status
              cancelReason: `Add-ons are not available on ${updatedOfferingName}`,
            };
          }
        }
      });
    } else {
      linkedEntitlementsOfContainer.forEach((linkedEntitlement) => {
        if (linkedEntitlement?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON) {
          const productStatus = selectedLinkedEntitlementsObj[linkedEntitlement.id]?.productStatuses ?? [];

          // filtering and removing the CANCEL status from the selectedLinkedEntitlements when the JSM offering is Premium
          const filteredProductStatus = productStatus.filter(
            (status: ProductStatus) => status !== ProductStatus.CANCEL
          );
          selectedLinkedEntitlementsObj[linkedEntitlement.id] = {
            ...selectedLinkedEntitlementsObj[linkedEntitlement.id],
            productStatuses: [...filteredProductStatus],
            cancelReason: undefined, // removing the cancel reason when the JSM offering is Premium
          };
        }
      });
    }
  }

  return {
    linkedEntitlements: linkedEntitlementsObj,
    selectedLinkedEntitlements: selectedLinkedEntitlementsObj,
  };
};
